/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2025 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { ClipEnhancementInfo, PlaylistClip } from "types/Clip";
import { BlockTypes } from "constants/BlockTypes";
import { AudioEffect } from "types/AudioEffect";
import { Asset } from "types/Asset";
import { AssetUrlType } from "types/AssetUrlCache";

export class UnmixedEffect extends AudioEffect {
  private readonly passthrough: GainNode;

  static override canBuildFor(clip: PlaylistClip): boolean {
    return true;
  }

  static override canUseLocalAsset(): boolean {
    return true;
  }

  static override providePermanentUrl(
    clip: PlaylistClip,
    asset: Asset,
    type: AssetUrlType
  ): string | undefined {
    return type === AssetUrlType.AUDIO
      ? asset.url
      : asset.streamingVideo360PUrl;
  }

  constructor(
    audioContext: AudioContext,
    clip: PlaylistClip,
    playlistTime: number
  ) {
    super(audioContext, clip, playlistTime);
    this.passthrough = this.audioContext.createGain();
  }

  hotswap(newClip: PlaylistClip): void {}

  disconnect(): void {
    this.passthrough.disconnect();
  }

  get inputNode(): AudioNode {
    return this.passthrough;
  }

  get outputNode(): AudioNode {
    return this.passthrough;
  }
}

export class EnhanceMixEffect extends AudioEffect {
  private readonly mixNode: GainNode;
  private readonly originalGain: GainNode;
  private readonly enhancedGain: GainNode;
  private readonly splitter: ChannelSplitterNode;

  static override providePermanentUrl(
    clip: PlaylistClip,
    asset: Asset
  ): string | undefined {
    if (!EnhanceMixEffect.canBuildFor(clip) || !asset.streamingAudioV1Url) {
      return undefined;
    }

    return asset.streamingAudioV1Url;
  }

  static override canUseLocalAsset(): boolean {
    return false;
  }

  static override canBuildFor(clip: PlaylistClip): clip is PlaylistClip & {
    type: BlockTypes.TRANSCRIPTION;
    enhancementInfo: ClipEnhancementInfo;
  } {
    return (
      clip?.type === BlockTypes.TRANSCRIPTION &&
      clip?.enhancementInfo !== undefined
    );
  }

  constructor(
    audioContext: AudioContext,
    clip: PlaylistClip,
    playlistTime: number
  ) {
    if (!EnhanceMixEffect.canBuildFor(clip)) {
      throw Error(
        "Can't apply enhance mix node to a clip without enhancementInfo"
      );
    }

    super(audioContext, clip, playlistTime);

    this.enhancedGain = this.audioContext.createGain();
    this.originalGain = this.audioContext.createGain();
    this.mixNode = this.audioContext.createGain();
    this.splitter = this.audioContext.createChannelSplitter(2);

    this.splitter.connect(this.originalGain, 0);
    this.splitter.connect(this.enhancedGain, 1);
    this.originalGain.connect(this.mixNode);
    this.enhancedGain.connect(this.mixNode);

    this.hotswap(clip);
  }

  hotswap(newClip: PlaylistClip): void {
    if (newClip.enhancementInfo === undefined) {
      return;
    }

    this.originalGain.gain.value =
      1 - newClip.enhancementInfo.strengthPercent / 100;

    this.enhancedGain.gain.value =
      newClip.enhancementInfo.strengthPercent / 100;
  }

  get inputNode(): AudioNode {
    return this.splitter;
  }

  get outputNode(): AudioNode {
    return this.mixNode;
  }

  disconnect(): void {
    this.splitter.disconnect();
    this.enhancedGain.disconnect();
    this.originalGain.disconnect();
    this.mixNode.disconnect();
  }
}
